import { useSelector } from 'react-redux';
import PreferredZipCodeSelector from 'selectors/user/preferredZipCodeSelector';
import UserSubscriptionsSelector from 'selectors/user/userSubscriptions/userSubscriptionsSelector';
import { shipToHomeSelector } from 'selectors/page/product/fulfillmentOptions/deliveryOptions/shipToHome/shipToHomeSelector';
import preferredStoreInfoSelector from 'selectors/user/preferredStoreInfoSelector';
import { isMultiStoreFilteringEnabledSelector } from 'viewModel/selectors/shopYourStore/isMultiStoreFilteringEnabledSelector';
import basketSelector from 'selectors/basket/basketSelector';
import { isUserSDUSubscriberSelector } from 'viewModel/selectors/user/isUserSDUSubscriberSelector';
import { isUserSDUTrialEligibleSelector } from 'viewModel/selectors/user/isUserSDUTrialEligibleSelector';
import { isSDUAddedToBasketSelector } from 'viewModel/selectors/basket/isSDUAddedToBasketSelector';
import { isSameDayCouponAppliedSelector } from 'viewModel/selectors/basket/isSameDayCouponAppliedSelector';
import { showAddDeliveryPriceToSDDSelector } from 'viewModel/selectors/testTarget/showAddDeliveryPriceToSDDSelector';
import skuUtils from 'utils/Sku';
import storeUtils from 'utils/Store';
import basketUtils from 'utils/Basket';
import stringUtils from 'utils/String';
import DeliveryOptionsUtils from 'utils/DeliveryOptions';
import ExtraProductDetailsUtils from 'utils/ExtraProductDetailsUtils';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import FulfillmentMethodsEN from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_en_US';
import FulfillmentMethodsFR from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_fr_CA';

const { preferredZipCodeSelector } = PreferredZipCodeSelector;
const { userSubscriptionsSelector } = UserSubscriptionsSelector;
const {
    getSDDCountdownMessage, isNDDEnabled, isNDDNewDeliveryName, computeSameDaySubTitle, isSDUAllowedForUser
} = DeliveryOptionsUtils;
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(FulfillmentMethodsEN, FulfillmentMethodsFR);

const buildSameDayDeliveryPanelMessage = ({
    currentSku, basket, currentProduct, displayOrderCutoffCountdown, isOutOfStock
}) => {
    const rawSameDayDeliveryMessage = currentSku?.sameDayDeliveryMessage;
    const layer1SameDayDeliveryMessage =
        isOutOfStock || !rawSameDayDeliveryMessage
            ? null
            : displayOrderCutoffCountdown
                ? getSDDCountdownMessage(rawSameDayDeliveryMessage)
                : rawSameDayDeliveryMessage;
    const sameDayItems = basketUtils.getSameDayItems(basket);
    const hasSameDayItems = basketUtils.hasSameDayItems();
    const sddRougeV2SameDayDeliveryMessage = hasSameDayItems && sameDayItems?.sameDayDeliveryMessage;
    const isSDDRougeTestV2 = Boolean(currentProduct?.SDDRougeTestThreshold);

    return isSDDRougeTestV2 && sddRougeV2SameDayDeliveryMessage ? sddRougeV2SameDayDeliveryMessage : layer1SameDayDeliveryMessage;
};

const computeShippingMethodNotAvailable = (currentProduct, currentSku) => {
    const isGiftCard = Boolean(currentSku && skuUtils.isGiftCard(currentSku));
    const isOnlineOnly = Boolean(currentSku && skuUtils.isOnlineOnly(currentSku));
    const isCustomSets = !!(skuUtils.isCustomSetsSingleSkuProduct(currentProduct) || skuUtils.isCustomSetsGroupedSkuProduct(currentProduct));

    return (
        isGiftCard || isOnlineOnly || isCustomSets || skuUtils.isGwp(currentSku) || skuUtils.isSample(currentSku) || !currentSku?.isSameDayEligibleSku
    );
};

const buildRougeV2FreeShippingMessage = ({ currentProduct, basket }) => {
    const isSDDRougeTestV2 = Boolean(currentProduct?.SDDRougeTestThreshold);

    if (!isSDDRougeTestV2) {
        return '';
    }

    const currentSDDRougeTestRemainToFreeShipping =
        currentProduct?.SDDRougeTestRemainToFreeShipping !== basket?.SDDRougeTestRemainToFreeShipping
            ? basket?.SDDRougeTestRemainToFreeShipping
            : currentProduct?.SDDRougeTestRemainToFreeShipping;
    const currentSDDRougeTestFreeShipping =
        currentProduct?.SDDRougeTestFreeShipping !== basket?.SDDRougeTestFreeShipping
            ? basket?.SDDRougeTestFreeShipping
            : currentProduct?.SDDRougeTestFreeShipping;

    if (currentSDDRougeTestRemainToFreeShipping && !currentSDDRougeTestFreeShipping) {
        return stringUtils.format(
            isNDDNewDeliveryName() ? getText('fastDeliveryRougeTestV2Message', ['{0}']) : getText('sddRougeTestV2Message', ['{0}']),
            currentSDDRougeTestRemainToFreeShipping
        );
    }

    return isNDDNewDeliveryName() ? getText('fastDeliveryRougeTestV2FreeShippingMessage') : getText('sddRougeTestV2FreeShippingMessage');
};

/**
 * Derives the Pickup selected-state fields (SHP-5238) — mirrors the legacy
 * `BOPIS.f.jsx` derivations (store name, availability label/color, readiness
 * message color, and the in-store/curbside pickup indicators). Extracted from
 * the hook body so `useFulfillmentMethodsProps` stays within the complexity
 * budget. `availabilityLabel` is returned as a locale key so the consumer
 * resolves it with its own `getText`, keeping this hook locale-agnostic.
 *
 * SHP-5280 adds the nearby-store availability signals used when the item is out
 * of stock at the selected store: `isInStockAtNearbyStore` and
 * `isOutOfStockAtNearbyStore` (within 50 miles). Both mirror the legacy
 * `useDeliveryOptionsProps` derivation, are gated on the Multi-Store Filtering
 * flag, and are only ever true while the item is out of stock at the selected
 * store. `showNearbyStoreAvailability` is their union, so the consumer can gate
 * the below-cards nearby row on a single boolean without re-deriving the OOS
 * state.
 *
 * @param {object} params
 * @param {object} [params.currentProduct] - The current product from Redux.
 * @param {object} [params.currentSku] - The current SKU.
 * @param {object} params.preferredStoreInfo - The user's preferred store slice.
 * @param {boolean} params.isMultiStoreFilteringEnabled - Multi-Store Filtering gate.
 * @returns {object} Pickup fields consumed by the Pickup card and details panel.
 */
const buildPickupData = ({ currentProduct, currentSku, preferredStoreInfo, isMultiStoreFilteringEnabled }) => {
    const { isBopisable, isCurbsideEnabled, isConciergeCurbsideEnabled } = preferredStoreInfo;
    const isPickUpEligibleSku = Boolean(currentSku?.isPickUpEligibleSku);
    const availabilityStatus = currentSku?.actionFlags?.availabilityStatus;
    const isReservationNotOffered = Boolean(currentSku?.actionFlags?.isReservationNotOffered);
    const isPickupOutOfStock = ExtraProductDetailsUtils.isOutOfStock(availabilityStatus);
    const availabilityColor = !availabilityStatus ? undefined : isReservationNotOffered ? 'gray' : isPickupOutOfStock ? 'red' : 'green';
    const pickupMessageColor = isReservationNotOffered || isPickupOutOfStock ? 'gray' : 'green';
    const showInStorePickup = Boolean(isBopisable && isPickUpEligibleSku && !isPickupOutOfStock && !isReservationNotOffered);

    const nearbyAvailabilityStatus = currentSku?.actionFlags?.nearbyAvailabilityStatus;
    const showNearbyAvailability = isMultiStoreFilteringEnabled && isPickupOutOfStock;
    const isInStockAtNearbyStore = showNearbyAvailability && ExtraProductDetailsUtils.isinStock(nearbyAvailabilityStatus);
    const isOutOfStockAtNearbyStore = showNearbyAvailability && ExtraProductDetailsUtils.isOutOfStock(nearbyAvailabilityStatus);
    const showNearbyStoreAvailability = isInStockAtNearbyStore || isOutOfStockAtNearbyStore;

    return {
        storeName: storeUtils.getStoreDisplayName(preferredStoreInfo),
        availabilityLabel: ExtraProductDetailsUtils.availabilityLabel(availabilityStatus),
        availabilityColor,
        hasStoreAvailability: Boolean(availabilityStatus),
        pickupMessage: currentProduct?.pickupMessage,
        pickupMessageColor,
        showInStorePickup,
        showCurbsideIndicator: showInStorePickup && !isConciergeCurbsideEnabled && Boolean(isCurbsideEnabled),
        showConciergeCurbsideIndicator: showInStorePickup && Boolean(isConciergeCurbsideEnabled),
        isInStockAtNearbyStore,
        isOutOfStockAtNearbyStore,
        showNearbyStoreAvailability,
        isPickupOutOfStock
    };
};

/**
 * Reads the Redux slices the Fulfillment Methods orchestrator needs and
 * derives the Fast Delivery card + panel fields (title, ETA subtitle,
 * availability/location row, cutoff message, colors, Rouge V2 SDD promo
 * message, and SDU trial eligibility). The Fast Delivery card subtitle is
 * produced via `DeliveryOptionsUtils.computeSameDaySubTitle`, the same
 * shared helper `useDeliveryOptionsProps` uses, so its 4-priority chain
 * (pricing AB test → promo → SLA → "not available") stays in one place.
 *
 * SHP-5238 adds the Pickup selected-state data. It mirrors the legacy `BOPIS.f.jsx`
 * derivations (store name, availability label/color, readiness message color, and
 * the in-store/curbside pickup indicators) so the redesigned Pickup card and
 * details panel show the same store data the legacy layout does — UI only, no
 * backend changes. `availabilityLabel` is returned as a locale key so the consumer
 * resolves it with its own `getText`, keeping this hook locale-agnostic. The raw
 * `pickupMessage` is returned untransformed; the panel applies the cutoff countdown
 * because that depends on the `displayOrderCutoffCountdown` test-target prop.
 *
 * @param {object} [currentProduct] - The current product from Redux.
 * @param {boolean} [displayOrderCutoffCountdown] - Legacy AB test flag that
 *   toggles the live countdown format on the SDD message.
 * @param {object} [fulfillment] - Fulfillment state from `useFulfillment`:
 *   `{ sameDayAvailable, sameDayNotAvailableForZip, serviceUnavailable }`.
 */
export default function useFulfillmentMethodsProps(currentProduct, displayOrderCutoffCountdown, fulfillment) {
    const { sameDayNotAvailableForZip, sameDayAvailable = !sameDayNotAvailableForZip, serviceUnavailable = false } = fulfillment || {};

    const preferredZipCode = useSelector(preferredZipCodeSelector);
    const shipToHome = useSelector(shipToHomeSelector);
    const preferredStoreInfo = useSelector(preferredStoreInfoSelector);
    const isMultiStoreFilteringEnabled = useSelector(isMultiStoreFilteringEnabledSelector);
    const basket = useSelector(basketSelector);
    const userSubscriptions = useSelector(userSubscriptionsSelector);
    const isUserSDUSubscriber = useSelector(isUserSDUSubscriberSelector);
    const isUserSDUTrialEligible = useSelector(isUserSDUTrialEligibleSelector);
    const isSDUAddedToBasket = useSelector(isSDUAddedToBasketSelector);
    const isSameDayCouponApplied = useSelector(isSameDayCouponAppliedSelector);
    const showDeliveryPrice = useSelector(showAddDeliveryPriceToSDDSelector);
    const isUserSDUTrialAllowed = isSDUAllowedForUser(userSubscriptions);

    const sduSubscriptions = userSubscriptions?.filter(subscription => subscription.type === 'SDU');
    const isUserSduTrialEligible = sduSubscriptions?.length > 0 ? sduSubscriptions[0]?.isTrialEligible : true;

    const sameDaySubTitle = computeSameDaySubTitle(
        currentProduct,
        sameDayAvailable,
        isUserSDUSubscriber,
        isUserSDUTrialEligible,
        showDeliveryPrice,
        isSDUAddedToBasket,
        isSameDayCouponApplied
    );

    const currentSku = currentProduct?.currentSku;
    const isReplenishmentEligible = Boolean(currentSku?.isReplenishmentEligible);
    const autoReplenProductEligibility =
        currentProduct?.regularChildSkus?.length > 0
            ? currentProduct.regularChildSkus.some(element => element?.isReplenishmentEligible)
            : isReplenishmentEligible;
    const showAutoReplenishment = Boolean(autoReplenProductEligibility && Sephora.configurationSettings.isAutoReplenishmentEnabled);
    const isGiftCard = Boolean(currentSku && skuUtils.isGiftCard(currentSku));
    const isOnlineOnly = Boolean(currentSku && skuUtils.isOnlineOnly(currentSku));
    const shippingMethodNotAvailable = computeShippingMethodNotAvailable(currentProduct, currentSku);
    const sddRadioButtonDisabled = shippingMethodNotAvailable || serviceUnavailable;

    const sameDayTitleText = isNDDNewDeliveryName() ? getText('fastDelivery') : currentSku?.sameDayTitle || getText('sameDayDelivery');

    const sameDayAvailabilityStatus = currentSku?.actionFlags?.sameDayAvailabilityStatus;
    const isSameDayEligibleSku = Boolean(currentSku?.isSameDayEligibleSku);
    const isOutOfStock = ExtraProductDetailsUtils.isOutOfStock(sameDayAvailabilityStatus);
    const availabilityLabelKey = sameDayAvailabilityStatus
        ? ExtraProductDetailsUtils.availabilityLabel(sameDayAvailabilityStatus)
        : 'selectForStoreAvailability';
    const availabilityText = sameDayNotAvailableForZip
        ? getText(isNDDNewDeliveryName() ? 'fastDeliveryNotAvailableForZip' : 'notAvailable')
        : getText(availabilityLabelKey);
    const locationText = preferredZipCode || getText('yourLocation');
    const forText = getText('for');
    const availabilityTextColor = !sameDayAvailabilityStatus ? null : !isSameDayEligibleSku ? 'gray' : isOutOfStock ? 'red' : 'green';

    const sameDayDeliveryPanelMessage = buildSameDayDeliveryPanelMessage({
        currentSku,
        basket,
        currentProduct,
        displayOrderCutoffCountdown,
        isOutOfStock
    });
    const sddRougeTestV2FreeShippingMessage = buildRougeV2FreeShippingMessage({ currentProduct, basket });
    const sddRougeTestThreshold = currentProduct?.SDDRougeTestThreshold;
    const nddEnabled = isNDDEnabled();

    const skuTrialEligibility = currentProduct?.SDUProduct?.skuTrialEligibility ?? false;
    const skuTrialPeriod = currentProduct?.SDUProduct?.skuTrialPeriod;

    const pickupData = buildPickupData({
        currentProduct,
        currentSku,
        preferredStoreInfo,
        isMultiStoreFilteringEnabled
    });

    return {
        preferredZipCode,
        shipToHomeMessage: shipToHome.shipToHomeMessage,
        showAutoReplenishment,
        isReplenishmentEligible,
        isGiftCard,
        isOnlineOnly,
        sddRadioButtonDisabled,
        availabilityText,
        locationText,
        forText,
        sameDayAvailabilityStatus,
        availabilityTextColor,
        sameDayDeliveryPanelMessage,
        sddRougeTestV2FreeShippingMessage,
        sddRougeTestThreshold,
        nddEnabled,
        isUserSDUSubscriber,
        isUserSduTrialEligible,
        isUserSDUTrialAllowed,
        isSDUAddedToBasket,
        skuTrialEligibility,
        skuTrialPeriod,
        sameDayTitleText,
        sameDaySubTitle,
        // Pickup selected-state data (SHP-5238)
        ...pickupData
    };
}
